import React, { useState, useEffect } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, ChevronRight, Loader2, Plus, RefreshCw, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc, updateDoc, collection } from 'firebase/firestore';

interface Tenancy {
  id: string;
  name: string;
  logo: string;
  plan: string;
  status?: 'active' | 'inactive' | 'suspended';
  lastAccessed?: string;
}

// Tenancy Card Component
interface TenancyCardProps {
  tenancy: Tenancy;
  isActive: boolean;
  onSwitch: (tenancy: Tenancy) => Promise<void>;
  isLoading: boolean;
}

const TenancyCard = React.memo(({
  tenancy,
  isActive,
  onSwitch,
  isLoading,
}: TenancyCardProps) => {
  const [logoError, setLogoError] = useState(false);

  return (
    <Card className={cn(
      'transition-all hover:shadow-md',
      isActive ? 'ring-2 ring-primary' : 'opacity-90 hover:opacity-100'
    )}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {!logoError && tenancy.logo ? (
              <img
                src={tenancy.logo}
                alt={`${tenancy.name} logo`}
                className="h-10 w-10 rounded-md object-cover"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                <Building className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <CardTitle className="text-lg font-medium">{tenancy.name}</CardTitle>
              <CardDescription className="text-xs">{tenancy.plan}</CardDescription>
            </div>
          </div>
          {isActive && (
            <Badge variant="default" className="ml-2">
              Active
            </Badge>
          )}
          <Button
            size="sm"
            onClick={() => onSwitch(tenancy)}
            disabled={isActive || isLoading}
            className="gap-1"
          >
          {isLoading && isActive ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {isActive ? 'Current' : 'Switch'}
        </Button>
        </div>
      </CardHeader>
      {/* <CardContent className="pb-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Status</p>
            <Badge variant={tenancy.status === 'active' ? 'success' : 'secondary'}>
              {tenancy.status || 'Inactive'}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Last Accessed</p>
            <p className="text-sm">
              {tenancy.lastAccessed ? new Date(tenancy.lastAccessed).toLocaleDateString() : 'Never'}
            </p>
          </div>
        </div>
      </CardContent> */}
    </Card>
  );
});

TenancyCard.displayName = 'TenancyCard';

// Main Component
const TenancyManagementPage = () => {
  const { activeTenancy, setActiveTenancy, tenancies: contextTenancies } = useTenancy();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenancies, setTenancies] = useState<Tenancy[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key state

  // Sync context tenancies with local state
  useEffect(() => {
    const updateTenancies = async () => {
      if (contextTenancies && contextTenancies.length > 0) {
        setTenancies(contextTenancies.map(t => ({
          ...t,
          id: t.id || '',
          plan: t.plan || 'Starter',
          status: 'active',
          lastAccessed: t.lastAccessed || new Date().toISOString(),
        })));
      }
      setIsRefreshing(false);
    };

    updateTenancies();
  }, [contextTenancies, refreshKey]); // Add refreshKey to dependency array

  // Handle tenancy switching - matches the TenancySwitcher implementation
  const handleSwitchTenancy = async (tenancy: Tenancy) => {
    setIsLoading(true);

    // Update lastAccessed timestamp
    const tenancyWithTimestamp = {
      ...tenancy,
      lastAccessed: new Date().toISOString()
    };

    // Save active tenancy to cache for instant reloads
    try {
      localStorage.setItem('active_tenancy_cache_v1', JSON.stringify(tenancyWithTimestamp));
    } catch (error) {
      console.error('Failed to save tenancy to cache:', error);
    }
    
    // Update the active tenancy in context
    setActiveTenancy(tenancyWithTimestamp);

    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Get the user's primary organization (first in the list)
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists) {
      setIsLoading(false);
      return;
    }

    const userData = userDoc.data();
    const primaryOrg = userData?.primaryTenancy || userData?.organisations?.[0];

    // If tenancy has an id property
    if (tenancy.id) {
      try {
        // If switching to primary organization
        if (primaryOrg === tenancy.id) {
          // Update Firebase first - set mspImpersonation to false and ensure organizations array only contains the primary org
          await updateDoc(userRef, {
            mspImpersonation: false,
            organisations: [primaryOrg], // Reset to only contain the primary organization
            primaryTenancy: primaryOrg // Add or update the primaryTenancy field
          });

          // Add a small delay to ensure the database update completes
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Then update the context and session storage
          sessionStorage.setItem('web-selectedTenancy', 'true');
          setActiveTenancy(tenancy);
          setIsLoading(false);
        } else {
          // Check if impersonation is allowed by fetching the tenancy details
          const tenancyRef = doc(db, 'customers', tenancy.id);
          const tenancyDoc = await getDoc(tenancyRef);

          if (tenancyDoc.exists) {
            const tenancyData = tenancyDoc.data();

            // Get MSP details
            let techMSP = tenancyData?.mspDetails?.techPartner?.msp || tenancyData?.mspDetails?.msp;
            let techImpersonationAllowed = tenancyData?.mspDetails?.techPartner?.impersonationAllowed || tenancyData?.mspDetails?.impersonationAllowed;

            let businessMSP = tenancyData?.mspDetails?.businessPartner?.msp || tenancyData?.mspDetails?.msp;
            let businessImpersonationAllowed = tenancyData?.mspDetails?.businessPartner?.impersonationAllowed || tenancyData?.mspDetails?.impersonationAllowed;

            // Check if impersonation is allowed
            if ((techMSP === primaryOrg && techImpersonationAllowed) || (businessMSP === primaryOrg && businessImpersonationAllowed)) {
              // Update Firebase first - ensure organizations array only contains primary org and the selected tenancy
              await updateDoc(userRef, {
                organisations: [primaryOrg, tenancy.id], // Only keep primary org and the selected tenancy
                mspImpersonation: true,
                primaryTenancy: primaryOrg // Add or update the primaryTenancy field
              });

              // Add a small delay to ensure the database update completes
              await new Promise((resolve) => setTimeout(resolve, 300));

              // Then update the context and session storage
              sessionStorage.setItem('web-selectedTenancy', 'true');
              setActiveTenancy(tenancy);
              setIsLoading(false);
            } else {
              // Show error or notification that impersonation is not allowed
              console.error('Impersonation not allowed for this tenancy');
              toast.error('Error', {
                description: 'You do not have permission to access this organization',
              });
              setIsLoading(false);
            }
          }
        }
      } catch (error) {
        console.error('Error switching tenancy:', error);
        toast.error('Error', {
          description: 'Failed to switch organization. Please try again.',
        });
        setIsLoading(false);
      }
    }
  };

  // Filter tenancies based on search query
  const filteredTenancies = tenancies.filter((tenancy) =>
    tenancy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenancy.plan.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mx-auto px-4 py-8">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tenancy Management</h1>
          <p className="text-muted-foreground">
            Manage and switch between your organizations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true);
              setRefreshKey(prev => prev + 1);
            }}
          >
            {isRefreshing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </>
            )}
          </Button>
          <Button size="sm" className="gap-1" onClick={() => window.location.href = '/onboarding-msp.html'}>
            <Plus className="h-4 w-4" />
            Add Organization
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search organizations..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {isLoading && tenancies.length === 0 ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading...</span>
        </div>
      ) : filteredTenancies.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTenancies.map((tenancy) => (
            <TenancyCard
              key={tenancy.id}
              tenancy={tenancy}
              isActive={activeTenancy?.id === tenancy.id}
              onSwitch={handleSwitchTenancy}
              isLoading={isLoading && activeTenancy?.id === tenancy.id}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-64 flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <Building className="h-10 w-10 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No organizations found</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {searchQuery ? 'Try a different search term' : 'Get started by adding a new organization'}
          </p>
          <Button 
            onClick={() => window.location.href = '/onboarding-msp.html'}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Organization
          </Button>
        </div>
      )}
    </div>
  );
};

export default TenancyManagementPage;
